import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Syringe, Leaf, X, ArrowRight, ArrowLeft, CheckCircle2 } from 'lucide-react';

/** Category → icon + accent color palette */
const CATEGORY_META = {
  filler: {
    icon: Syringe,
    accent: '#8948C3',
    accentSoft: 'rgba(137,72,195,0.18)',
    glow: 'rgba(137,72,195,0.55)',
  },
  botox: {
    icon: Sparkles,
    accent: '#40D3BE',
    accentSoft: 'rgba(64,211,190,0.22)',
    glow: 'rgba(64,211,190,0.55)',
  },
  general: {
    icon: Leaf,
    accent: '#C60C30',
    accentSoft: 'rgba(198,12,48,0.14)',
    glow: 'rgba(198,12,48,0.45)',
  },
};

function TipCard({ categoryKey, meta, onOpen, index, isAr }) {
  const { t } = useTranslation();
  const cat = t(`tips.categories.${categoryKey}`, { returnObjects: true }) || {};
  const Icon = meta.icon;
  const ArrowCta = isAr ? ArrowLeft : ArrowRight;

  return (
    <motion.button
      type="button"
      onClick={() => onOpen(categoryKey)}
      data-testid={`tip-card-${categoryKey}`}
      className="dwk-brand-card group text-left w-full"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.9, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -8 }}
      style={{
        background: `linear-gradient(160deg, ${meta.accentSoft} 0%, rgba(247,248,252,0.9) 60%)`,
        border: '1px solid rgba(16,9,46,0.06)',
      }}
    >
      <div
        className="absolute -inset-1 opacity-0 group-hover:opacity-100 blur-2xl transition-opacity duration-700 -z-10"
        style={{ background: `radial-gradient(closest-side, ${meta.glow}, transparent)` }}
        aria-hidden="true"
      />

      <div className="relative p-6 md:p-8 flex flex-col gap-6 min-h-[320px] md:min-h-[380px]">
        <div className="flex items-center justify-between">
          <span
            className="inline-flex w-14 h-14 md:w-16 md:h-16 rounded-2xl items-center justify-center text-white shadow-[0_10px_30px_-12px_rgba(16,9,46,0.35)]"
            style={{ background: meta.accent }}
          >
            <Icon size={26} />
          </span>
          <span
            className={`text-[10px] tracking-[0.35em] uppercase font-semibold ${isAr ? 'font-ar' : 'font-en'}`}
            style={{ color: meta.accent }}
          >
            {t('tips.preview_label')}
          </span>
        </div>

        <div className="mt-auto">
          <h3
            className={`text-2xl md:text-3xl font-bold text-[#10092E] tracking-tight ${isAr ? 'font-ar' : 'font-en'}`}
          >
            {cat.title}
          </h3>
          <p
            className={`mt-3 text-sm md:text-base text-[#10092E]/65 leading-relaxed line-clamp-3 ${isAr ? 'font-ar' : 'font-en'}`}
          >
            {cat.teaser}
          </p>

          <div
            className={`mt-6 inline-flex items-center gap-2 text-sm md:text-base font-semibold ${isAr ? 'font-ar' : 'font-en'}`}
            style={{ color: meta.accent }}
          >
            {t('tips.open')} <ArrowCta size={16} />
          </div>
        </div>
      </div>
    </motion.button>
  );
}

function TipsModal({ activeKey, onClose }) {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const isOpen = Boolean(activeKey);
  const cat = activeKey
    ? t(`tips.categories.${activeKey}`, { returnObjects: true }) || {}
    : {};
  const meta = activeKey ? CATEGORY_META[activeKey] : CATEGORY_META.filler;
  const Icon = meta.icon;
  const tips = Array.isArray(cat.tips) ? cat.tips : [];

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-center justify-center px-4 md:px-6 py-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          data-testid="tips-modal"
        >
          <button
            aria-label="Close overlay"
            onClick={onClose}
            className="absolute inset-0 bg-[#10092E]/70 backdrop-blur-xl"
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            className="relative w-full max-w-2xl rounded-3xl bg-white shadow-[0_40px_120px_-30px_rgba(16,9,46,0.55)] overflow-hidden max-h-[85vh] flex flex-col"
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            dir={isAr ? 'rtl' : 'ltr'}
          >
            <div
              className="relative px-6 md:px-10 pt-8 pb-6 flex items-start gap-4"
              style={{
                background: `linear-gradient(135deg, ${meta.accentSoft} 0%, rgba(255,255,255,0.85) 90%)`,
              }}
            >
              <div
                className="shrink-0 inline-flex w-14 h-14 md:w-16 md:h-16 rounded-2xl items-center justify-center text-white"
                style={{ background: meta.accent }}
              >
                <Icon size={26} />
              </div>
              <div className="min-w-0">
                <h3
                  className={`text-2xl md:text-3xl font-bold text-[#10092E] tracking-tight ${isAr ? 'font-ar' : 'font-en'}`}
                  data-testid="tips-modal-title"
                >
                  {cat.title}
                </h3>
                <p
                  className={`mt-2 text-sm md:text-base text-[#10092E]/65 leading-relaxed ${isAr ? 'font-ar' : 'font-en'}`}
                >
                  {cat.teaser}
                </p>
              </div>
              <button
                onClick={onClose}
                data-testid="tips-modal-close"
                aria-label={t('tips.close')}
                className={`shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full border border-[#10092E]/15 bg-white/80 backdrop-blur text-[#10092E] hover:border-[#8948C3] hover:text-[#8948C3] transition-colors ${isAr ? 'mr-auto' : 'ml-auto'}`}
              >
                <X size={16} />
              </button>
            </div>

            <ol
              className="px-6 md:px-10 py-6 md:py-8 flex-1 overflow-y-auto space-y-4"
              data-testid="tips-modal-list"
            >
              {tips.map((tip, i) => (
                <motion.li
                  key={i}
                  className="flex items-start gap-3"
                  initial={{ opacity: 0, x: isAr ? 20 : -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4, ease: 'easeOut' }}
                >
                  <CheckCircle2
                    size={20}
                    className="shrink-0 mt-0.5"
                    style={{ color: meta.accent }}
                    aria-hidden="true"
                  />
                  <p
                    className={`text-[15px] md:text-base text-[#10092E]/80 leading-relaxed ${isAr ? 'font-ar' : 'font-en'}`}
                  >
                    {tip}
                  </p>
                </motion.li>
              ))}
            </ol>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

export default function Tips() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const [activeKey, setActiveKey] = useState(null);

  return (
    <section
      id="tips"
      data-testid="tips-section"
      className="relative bg-[#F7F8FC]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-32">
        <div
          className="text-[14px] md:text-[15px] tracking-[0.32em] uppercase text-[#8948C3] mb-6 font-semibold"
          data-testid="tips-eyebrow"
        >
          {t('tips.eyebrow')}
        </div>
        <h2
          className={`text-[#10092E] font-bold tracking-tight leading-[1.05] ${isAr ? 'font-ar text-5xl md:text-7xl' : 'font-en text-5xl md:text-7xl'}`}
          data-testid="tips-heading"
        >
          {t('tips.heading')}
        </h2>
        <p
          className={`mt-6 max-w-2xl text-[#10092E]/60 leading-relaxed ${isAr ? 'font-ar text-base md:text-lg' : 'font-en text-base md:text-lg'}`}
        >
          {t('tips.subheading')}
        </p>
        <div className="hairline mt-14" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {['filler', 'botox', 'general'].map((key, i) => (
            <TipCard
              key={key}
              categoryKey={key}
              meta={CATEGORY_META[key]}
              onOpen={setActiveKey}
              index={i}
              isAr={isAr}
            />
          ))}
        </div>
      </div>

      <TipsModal activeKey={activeKey} onClose={() => setActiveKey(null)} />
    </section>
  );
}
